<template>
  <div class="filter-radio-buttons row-center gap-1.5 min-w-0 max-w-full">
    <i
      v-if="iconName"
      :class="`ti ti-${iconName} ${emphasisFont[size]} color-${iconColor} shrink-0`"
      aria-hidden="true"
    ></i>
    <IonSegment
      ref="segmentRef"
      :id="inputId"
      scrollable
      :value="selectedValue"
      class="filter-radio-buttons-track min-w-0 max-w-full"
      :class="`size-${size}`"
      :style="segmentColorStyle"
      @ionChange="onFilterChange($event)"
    >
      <IonSegmentButton
        v-for="option in radioItems"
        :key="option.Id"
        :value="String(option.Id)"
        :disabled="option.IsDisabled"
        :class="option.IsDisabled ? 'pointer-events-none cursor-not-allowed opacity-40' : ''"
        :style="segmentButtonStyle"
      >
        <IonLabel :class="[captionFont[size], 'whitespace-nowrap']">
          {{ option.Name }}
        </IonLabel>
      </IonSegmentButton>
    </IonSegment>
  </div>
</template>
<script lang="ts">
import { CAPTION_FONT, EMPHASIS_FONT } from "@/constants/sizing";
import { ComponentColorType, ComponentSizeType } from "@/types/componentType";
import { InputOption } from "@/types/global";
import { IonLabel, IonSegment, IonSegmentButton } from "@ionic/vue";
import { computed, CSSProperties, defineComponent, PropType, ref, watch } from "vue";

// Ionic's ion-segment treats ANY segment nested inside an ion-segment-view as that view's
// tab bar: its body-level `ionSegmentViewScroll` listener (segment.js
// handleSegmentViewScroll, the `dispatchedFrom.contains(segmentEl)` branch) re-checks a
// button picked by scroll ratio and emits ionChange — so a layout-shift scroll of the view
// fires phantom selections on every nested filter. That whole cascade runs SYNCHRONOUSLY
// inside the ionSegmentViewScroll dispatch, and Ionic's listener is bubble-phase on body —
// a document-level CAPTURE listener runs first. Flag the dispatch window here (cleared on
// the next microtask): an ionChange arriving inside it is a scroll-sync phantom, never a
// user selection.
let isSegmentViewScrollDispatching = false;
if (typeof document !== "undefined") {
  document.addEventListener(
    "ionSegmentViewScroll",
    () => {
      isSegmentViewScrollDispatching = true;
      queueMicrotask(() => {
        isSegmentViewScrollDispatching = false;
      });
    },
    true,
  );
}

export default defineComponent({
  name: "CustomIonFilterRadioButtons",
  components: { IonSegment, IonSegmentButton, IonLabel },
  props: {
    inputId: { type: String, required: true },
    radioItems: { type: Array as PropType<Array<InputOption>>, required: true },
    initialValue: { type: [Number, String], default: null },
    color: { type: String as PropType<ComponentColorType>, default: "primary" },
    size: { type: String as PropType<ComponentSizeType>, default: "default" },
    slideWidth: { type: String, default: "auto" },
    iconName: { type: String, default: null },
    iconColor: { type: String, default: "tertiary" },
  },
  emits: ["onChange"],
  setup(props, { emit }) {
    const toSegmentValue = (value: number | string | null): string | undefined =>
      value === null || value === undefined ? undefined : String(value);
    const selectedValue = ref<string | undefined>(toSegmentValue(props.initialValue));
    const segmentRef = ref<InstanceType<typeof IonSegment> | null>(null);
    const segmentColorStyle = computed<CSSProperties>(() => ({
      "--background": `var(--color-${props.color}-bg)`,
    }));
    const segmentButtonStyle = computed<CSSProperties>(() => {
      const style: CSSProperties = {
        "--background-checked": `var(--color-${props.color})`,
        "--indicator-color": `var(--color-${props.color})`,
        "--color": `var(--color-${props.color}-dark)`,
        "--color-checked": "var(--color-light)",
      };
      if (props.slideWidth !== "auto") {
        style.minWidth = props.slideWidth;
      }
      return style;
    });

    watch(
      () => props.initialValue,
      (newValue) => {
        selectedValue.value = toSegmentValue(newValue);
      },
    );

    function onFilterChange(event: CustomEvent) {
      const newValue = event.detail.value as string;
      if (isSegmentViewScrollDispatching) {
        // Phantom scroll-sync change: undo Ionic's button re-check and swallow the event
        // (a programmatic value write does not re-emit ionChange).
        const segmentEl = segmentRef.value?.$el as HTMLElement & { value?: string };
        if (segmentEl) {
          segmentEl.value = selectedValue.value;
        }
        return;
      }
      const currentOption = props.radioItems.find((option) => String(option.Id) === newValue);
      if (!currentOption || currentOption.IsDisabled) {
        return;
      }
      selectedValue.value = newValue;
      emit("onChange", currentOption.Id);
    }

    return {
      segmentRef,
      selectedValue,
      segmentColorStyle,
      segmentButtonStyle,
      emphasisFont: EMPHASIS_FONT,
      captionFont: CAPTION_FONT,
      onFilterChange,
    };
  },
});
</script>
<style lang="scss" scoped>
.filter-radio-buttons-track {
  --border-radius: var(--rounded-radius);
  border-radius: var(--rounded-radius);
}
ion-segment-button {
  --border-radius: var(--rounded-radius);
  --indicator-box-shadow: none;
  margin-inline-start: 2px;
  margin-inline-end: 2px;
}
.size-small {
  --min-height: var(--control-height-small);
}
.size-default {
  --min-height: var(--control-height-default);
}
.size-large {
  --min-height: var(--control-height-large);
}
</style>
